import {
  Button,
  Colors,
  MenuDivider,
  MenuItem,
  ResizeEntry,
  ResizeSensor,
  Spinner,
} from "@blueprintjs/core";
import { Editor } from "@tinymce/tinymce-react";
import DOMPurify from "dompurify";
import { getIn } from "formik";
import { FGCustomInput, IFGFormGroupProps, useFGContext } from "nsitools-react";
import * as React from "react";
import { useQuery } from "react-query";
import styled from "styled-components";

import { useCommonHooks } from "../../hooks";
import { ETLCodes } from "../../locales";
import { LoadingOverlay } from "../LoadingOverlay";

const TOOLBAR =
  "undo redo | blocks fontsize | " +
  "bold italic underline strikethrough forecolor backcolor | " +
  "alignleft aligncenter alignright alignjustify | " +
  "bullist numlist outdent indent | link image table | removeformat code";

const PLUGINS = [
  "advlist",
  "anchor",
  "autolink",
  "charmap",
  "code",
  "fullscreen",
  "image",
  "insertdatetime",
  "link",
  "lists",
  "media",
  "preview",
  "searchreplace",
  "table",
  "visualblocks",
];

const DEFAULT_HEIGHT = 500;

const Container = styled.div<{ width: string }>`
  display: flex;
  flex: 1;

  & .editor-container {
    flex: 3;
  }

  & .tox-tinymce {
    width: ${(props) => props.width};
  }
`;

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MiddleIconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-right: solid 1px ${Colors.LIGHT_GRAY3};
  padding: 0 0.5rem;
`;

const MenuContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  & *:focus {
    outline: none !important;
  }
`;

const MenuStyled = styled.div`
  background: transparent !important;
  flex: 1;
  & li {
    list-style-type: none;
  }
`;

export interface IFGMailEditorProps
  extends Omit<IFGFormGroupProps, "children"> {
  placeholder?: string;
  growVertically?: boolean;
  rows?: number;
  readonly?: boolean;
  disabled?: boolean;
  tabIndex?: number;
  height?: string;
  loading?: boolean;
}

export const FGMailEditor: React.FunctionComponent<IFGMailEditorProps> = ({
  growVertically,
  rows,
  placeholder,
  readonly = false,
  disabled,
  tabIndex,
  loading,
  height,
  name,
  ...formGroupProps
}) => {
  const { apis, t } = useCommonHooks();
  const {
    formik,
    editMode,
    loading: globalLoading,
    disabled: globalDisabled,
  } = useFGContext();

  const { data: variables, isFetching: loadingVariables } = useQuery(
    "referential-variables",
    React.useCallback(
      async () => await apis.referentials.variables(),
      [apis.referentials]
    )
  );

  const finalData = React.useMemo(() => {
    if (formik && formik?.values) {
      const value = getIn(formik.values, name) as string;
      if (value) {
        return value;
      }
    }
    return "";
  }, [name, formik]);

  const [showVariablesPane, setShowVariablesPane] = React.useState(editMode);

  const onEditorChange = React.useCallback(
    (value: string) => {
      if (value !== getIn(formik.values, name))
        formik.setFieldValue(name, value);
    },
    [name, formik]
  );
  const editorInstance = React.useRef<any>();

  // La valeur est poussée explicitement dans formik : l'insertion se fait au clic sur le
  // panneau de variables, donc après le blur de l'éditeur. S'appuyer sur les évènements de
  // l'éditeur perdait les variables ajoutées.
  const writeAtCursor = React.useCallback(
    (text: string) => {
      const editor = editorInstance.current;
      if (!editor) return;
      editor.insertContent(text);
      formik.setFieldValue(name, editor.getContent());
    },
    [formik, name]
  );

  const onAddFieldDialogClosed = React.useCallback(
    (value: string) => {
      const formattedValue = `{{${value}}}`;
      writeAtCursor(formattedValue);
    },
    [writeAtCursor]
  );

  const onEditorReady = React.useCallback((instance: any) => {
    editorInstance.current = instance;
  }, []);

  const editorHeight = React.useMemo(
    () => parseInt(height, 10) || DEFAULT_HEIGHT,
    [height]
  );

  const [componentSize, setComponentSize] = React.useState<ResizeEntry>();
  const handleResize = React.useCallback((entries: ResizeEntry[]) => {
    setComponentSize(entries.reverse()[0]);
  }, []);

  return (
    <ResizeSensor onResize={handleResize}>
      <Container width={showVariablesPane ? "580px" : "818px"}>
        {disabled || globalDisabled || readonly ? (
          <div
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(finalData) }}
          />
        ) : (
          <FGCustomInput
            name={name}
            noLabel
            className={"editor-container " + formGroupProps.className}
          >
            {(ctx) => (
              <>
                <LoadingOverlay
                  loading={globalLoading || loading}
                  overlayHeight={componentSize?.contentRect.height}
                  overlayWidth={componentSize?.contentRect.width}
                ></LoadingOverlay>
                <Editor
                  tinymceScriptSrc="/tinymce/tinymce.min.js"
                  value={finalData}
                  onEditorChange={onEditorChange}
                  init={{
                    language: "fr_FR",
                    height: editorHeight,
                    width: "100%",
                    branding: false,
                    menubar: false,
                    elementpath: false,
                    convert_urls: false,
                    plugins: PLUGINS,
                    toolbar: TOOLBAR,
                    setup: onEditorReady,
                  }}
                />
              </>
            )}
          </FGCustomInput>
        )}
        {editMode && (
          <MenuContainer
            style={{
              flex: showVariablesPane ? 1 : null,
            }}
          >
            <MiddleIconContainer>
              <Button
                icon={showVariablesPane ? "chevron-right" : "chevron-left"}
                minimal
                onClick={() => setShowVariablesPane((prev) => !prev)}
                style={{ height: "100%" }}
              />
            </MiddleIconContainer>
            <MenuStyled
              style={{
                maxWidth: showVariablesPane ? "100%" : 0,
                visibility: showVariablesPane ? "visible" : "hidden",
              }}
            >
              <MenuDivider title={t(ETLCodes.Variables)} />
              {loadingVariables ? (
                <SpinnerContainer>
                  <Spinner />
                </SpinnerContainer>
              ) : (
                variables?.map((v) => (
                  <MenuItem
                    key={v.idValue}
                    text={v.displayValue}
                    onClick={(e) => onAddFieldDialogClosed(v.idValue)}
                  />
                ))
              )}
            </MenuStyled>
          </MenuContainer>
        )}
      </Container>
    </ResizeSensor>
  );
};

export default FGMailEditor;
